import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";

import Index from "./pages/Index";
const ProfessoresVSL = lazy(() => import("./pages/ProfessoresVSL"));
const AposentadosPensionistas = lazy(
  () => import("./pages/AposentadosPensionistas"),
);
const ImpostoDeRenda = lazy(() => import("./pages/ImpostoDeRenda"));
const ServidoresSaude = lazy(() => import("./pages/ServidoresSaude"));
const Policiais = lazy(() => import("./pages/Policiais"));

const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RedesSociais = lazy(() => import("./pages/RedesSociais"));
const Bio = lazy(() => import("./pages/Bio"));
const AreasDeAtuacao = lazy(() => import("./pages/AreasDeAtuacao"));
const Avaliacoes = lazy(() => import("./pages/Avaliacoes"));
const Sobre = lazy(() => import("./pages/Sobre"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPostProfessores = lazy(() => import("./pages/BlogPostProfessores"));
const BlogPostAposentados = lazy(() => import("./pages/BlogPostAposentados"));
const BlogPostImpostoRenda = lazy(() => import("./pages/BlogPostImpostoRenda"));
const BlogPostSaude = lazy(() => import("./pages/BlogPostSaude"));
const BlogPostPoliciais = lazy(() => import("./pages/BlogPostPoliciais"));
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import { useSmoothScroll } from "./hooks/useSmoothScroll";
import type {} from "./hooks/useSmoothScroll"; // ensures Window.__lenis type is loaded
import { AnimatePresence } from "framer-motion";
import PageTransition from "./components/PageTransition";
import { HelmetProvider } from "react-helmet-async";

const queryClient = new QueryClient();

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    // Use Lenis if available, otherwise fall back to native scroll
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [pathname]);
  return null;
};

const SmoothScrollWrapper = ({ children }: { children: React.ReactNode }) => {
  useSmoothScroll();
  return <>{children}</>;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={
              <PageTransition>
                <Index />
              </PageTransition>
            }
          />
          <Route
            path="/politica-de-privacidade"
            element={
              <PageTransition>
                <PrivacyPolicy />
              </PageTransition>
            }
          />
          <Route
            path="/redes-sociais"
            element={
              <PageTransition>
                <RedesSociais />
              </PageTransition>
            }
          />
          <Route
            path="/bio"
            element={
              <PageTransition>
                <Bio />
              </PageTransition>
            }
          />
          <Route
            path="/areas-de-atuacao/professores-quinquenios"
            element={
              <PageTransition>
                <ProfessoresVSL />
              </PageTransition>
            }
          />
          <Route
            path="/areas-de-atuacao/aposentados-e-pensionistas"
            element={
              <PageTransition>
                <AposentadosPensionistas />
              </PageTransition>
            }
          />
          <Route
            path="/areas-de-atuacao/imposto-de-renda"
            element={
              <PageTransition>
                <ImpostoDeRenda />
              </PageTransition>
            }
          />
          <Route
            path="/areas-de-atuacao/servidores-da-saude"
            element={
              <PageTransition>
                <ServidoresSaude />
              </PageTransition>
            }
          />
          <Route
            path="/areas-de-atuacao/policiais"
            element={
              <PageTransition>
                <Policiais />
              </PageTransition>
            }
          />
          <Route
            path="/areas-de-atuacao"
            element={
              <PageTransition>
                <AreasDeAtuacao />
              </PageTransition>
            }
          />
          <Route
            path="/avaliacoes"
            element={
              <PageTransition>
                <Avaliacoes />
              </PageTransition>
            }
          />
          <Route
            path="/sobre"
            element={
              <PageTransition>
                <Sobre />
              </PageTransition>
            }
          />
          <Route
            path="/blog"
            element={
              <PageTransition>
                <Blog />
              </PageTransition>
            }
          />
          <Route
            path="/blog/acoes-para-professores"
            element={
              <PageTransition>
                <BlogPostProfessores />
              </PageTransition>
            }
          />
          <Route
            path="/blog/acoes-para-aposentados-e-pensionistas"
            element={
              <PageTransition>
                <BlogPostAposentados />
              </PageTransition>
            }
          />
          <Route
            path="/blog/imposto-de-renda"
            element={
              <PageTransition>
                <BlogPostImpostoRenda />
              </PageTransition>
            }
          />
          <Route
            path="/blog/acoes-para-servidores-da-saude"
            element={
              <PageTransition>
                <BlogPostSaude />
              </PageTransition>
            }
          />
          <Route
            path="/blog/acoes-para-policiais"
            element={
              <PageTransition>
                <BlogPostPoliciais />
              </PageTransition>
            }
          />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route
            path="*"
            element={
              <PageTransition>
                <NotFound />
              </PageTransition>
            }
          />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <SmoothScrollWrapper>
            <ScrollToTop />
            <AnimatedRoutes />
            <FloatingWhatsApp />
          </SmoothScrollWrapper>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
